import React, { useState, useEffect } from "react";
import { compose } from "react-apollo";
import { withQuery } from "containers/withQuery";
import styled from "styled-components";
import Input from "components/Input/Input";
import Modal from "components/Modal/Modal";
import Field from "components/Field/Field";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import Footer from "../modalComponents/Footer";
import { actuatorTypes } from "../enumTypes";
import { Select } from "../CustomSelect";
import { PLANT_LOCATIONS_QUERY } from "../generalRegisterQueries";

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 35px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  input[type="checkbox"] {
    width: 22px;
    height: 22px;
    cursor: pointer;
  }

  label {
    margin: 0;
    font-weight: 500;
  }
`;

const ACTUATOR_TYPES = ["pulse", "keep", "hold"];

const buildInitialState = ({
  name,
  plantLocation,
  type,
  maintenanceMode,
  isCreate,
}) => ({
  name: name || "",
  maintenanceMode,
  type,
  plantLocationId: plantLocation ? plantLocation.id : null,
  ...(isCreate ? { status: false } : {})
});

const ActuatorModal = ({
  name,
  plantLocation,
  type,
  maintenanceMode,
  plantLocations,
  isCreate,
  snackbarContext,
  onSave,
  isOpen,
  toggleModal,
  title,
}) => {
  const [form, setForm] = useState(
    buildInitialState({
      name,
      plantLocation,
      type,
      maintenanceMode,
      isCreate,
    })
  );

  const actuatorTypeOptions = [
    {
      value: null,
      isDisabled: true,
    },
    ...ACTUATOR_TYPES.map((actuatorType) => ({
      value: actuatorType,
      label: actuatorTypes[actuatorType],
    })),
  ];

  const plantLocationOptions = [
    {
      value: null,
      isDisabled: true,
    },
    ...[...plantLocations]
      .sort((a, b) => a.id - b.id)
      .map(location => ({
        value: location.id,
        label: location.name,
      }))
  ];

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialState({
        name,
        plantLocation,
        type,
        maintenanceMode,
        isCreate,
      }));
    }
  }, [isOpen, name, plantLocation, type, maintenanceMode, isCreate]);

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const onSaveEntry = () => {
    if (!form.name.trim() || !form.type || !form.plantLocationId) {
      snackbarContext.setNotificationTimeOut(
        Color.danger,
        "Os campos nome, tipo e card são obrigatórios"
      );
      return;
    }

    onSave({
      ...form,
      maintenanceMode: form.maintenanceMode || false,
    });
  };

  const updateSelect = (key) => (_, option) => {
    setForm(prev => ({
      ...prev,
      [key]: option ? option.value : null
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      toggleModal={toggleModal}
      size="md"
      title={title}
      footer={<Footer onConfirm={onSaveEntry} />}
    >
      <Row>
        <Field title="Nome" width="48%">
          <Input
            name="name"
            style={{ minHeight: "40px" }}
            value={form.name}
            onChange={updateField}
          />
        </Field>
        <Field title="Tipo" width="48%">
          <Select
            name="type"
            placeholder="Escolha..."
            value={actuatorTypeOptions.find(o => o.value === form.type) || null}
            onChange={updateSelect("type")}
            options={actuatorTypeOptions}
          />
        </Field>
      </Row>
      <Row>
        <Field title="Card" width="48%">
          <Select
            name="plantLocationId"
            placeholder="Escolha..."
            value={plantLocationOptions.find(o => o.value === form.plantLocationId) || null}
            onChange={updateSelect("plantLocationId")}
            options={plantLocationOptions}
          />
        </Field>
        <Field title="Modo de Manutenção" width="48%">
          <CheckboxRow>
            <div style={{ paddingLeft: 20, paddingBottom: 5 }}>
              <Input
                name="maintenanceMode"
                type="checkbox"
                checked={!!form.maintenanceMode}
                onChange={updateField}
              />
            </div>
          </CheckboxRow>
        </Field>
      </Row>
    </Modal>
  );
};

export default compose(
  withQuery({ query: PLANT_LOCATIONS_QUERY }),
  withSnackbarConsumer,
)(ActuatorModal);
